import { useState, useEffect } from 'react';
import { supabase } from '../supabase';

const GOLD = '#FFC220';

const BADGES = [
  'New Location',
  'Reminder',
  'Update',
  'Promotion',
  'Alert',
  'Event',
];

const emptyForm = {
  headline: '',
  badge: 'Update',
  date: '',
  body: '',
  logo_url: '',
  active_fmp: true,
  active_athletics: false,
};

export default function News() {
  const [newsItems, setNewsItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');

  useEffect(() => {
    fetchNews();
  }, []);

  const fetchNews = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('news')
      .select('*')
      .order('created_at', { ascending: false });

    if (!error) setNewsItems(data);
    setLoading(false);
  };

const handleEdit = (item) => {
    setForm({
      headline: item.headline || '',
      badge: item.badge || 'Update',
      date: item.date || '',
      body: item.body || '',
      logo_url: item.logo_url || '',
      active_fmp: item.active_fmp,
      active_athletics: item.active_athletics,
    });
    setEditingId(item.id);
    setShowForm(true);
    window.scrollTo(0, 0);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this news item?')) return;
    await supabase.from('news').delete().eq('id', id);
    fetchNews();
  };

  const handleToggle = async (item, field) => {
    await supabase
      .from('news')
      .update({ [field]: !item[field] })
      .eq('id', item.id);
    fetchNews();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.headline) {
      alert('Please enter a headline.');
      return;
    }

    setSaving(true);

    if (editingId) {
      await supabase
        .from('news')
        .update(form)
        .eq('id', editingId);
      setSuccessMsg('News item updated successfully!');
    } else {
      await supabase
        .from('news')
        .insert(form);
      setSuccessMsg('News item added successfully!');
    }

    setTimeout(() => setSuccessMsg(''), 3000);
    setForm(emptyForm);
    setEditingId(null);
    setShowForm(false);
    setSaving(false);
    fetchNews();
  };

  const handleCancel = () => {
    setForm(emptyForm);
    setEditingId(null);
    setShowForm(false);
  };

  if (loading) return (
    <div style={styles.loading}><p style={{ color: GOLD }}>Loading...</p></div>
  );

  return (
    <div style={styles.wrap}>

      {/* TOPBAR */}
      <div style={styles.topbar}>
        <h1 style={styles.topbarTitle}>
          News
          <span style={styles.topbarCount}>{newsItems.length} items</span>
        </h1>
        <button
          style={styles.topbarBtn}
          onClick={() => {
            setShowForm(!showForm);
            setEditingId(null);
            setForm(emptyForm);
          }}
        >
          {showForm && !editingId ? '✕ Cancel' : '+ Add News'}
        </button>
      </div>

      <div style={styles.content}>

        {/* SUCCESS */}
        {successMsg && (
          <div style={styles.successBanner}>✅ {successMsg}</div>
        )}

        {/* FORM */}
        {showForm && (
          <div style={styles.formCard}>
            <h2 style={styles.formTitle}>
              {editingId ? 'Edit News Item' : 'Add News Item'}
            </h2>
            <form onSubmit={handleSubmit}>

              {/* Headline */}
              <div style={styles.formRow}>
                <label style={styles.label}>HEADLINE *</label>
                <input
                  style={styles.input}
                  type="text"
                  placeholder="e.g. Aviano Coffee is now accepting the Gold Card!"
                  value={form.headline}
                  onChange={(e) => setForm({ ...form, headline: e.target.value })}
                  required
                />
              </div>

              {/* Body */}
              <div style={styles.formRow}>
                <label style={styles.label}>BODY TEXT</label>
                <textarea
                  style={styles.textarea}
                  placeholder="Expanded description that users will see when they tap the headline..."
                  value={form.body}
                  onChange={(e) => setForm({ ...form, body: e.target.value })}
                  rows={4}
                />
              </div>

              {/* Logo URL */}
              <div style={styles.formRow}>
                <label style={styles.label}>LOGO URL</label>
                <input
                  style={styles.input}
                  type="text"
                  placeholder="https://... (paste Supabase storage URL)"
                  value={form.logo_url}
                  onChange={(e) => setForm({ ...form, logo_url: e.target.value })}
                />
                {form.logo_url && (
                  <img
                    src={form.logo_url}
                    alt="Logo preview"
                    style={styles.logoPreview}
                  />
                )}
              </div>

              <div style={styles.formGrid}>

                {/* Badge */}
                <div style={styles.formRow}>
                  <label style={styles.label}>BADGE</label>
                  <select
                    style={styles.select}
                    value={form.badge}
                    onChange={(e) => setForm({ ...form, badge: e.target.value })}
                  >
                    {BADGES.map((b) => (
                      <option key={b} value={b}>{b}</option>
                    ))}
                  </select>
                </div>

                {/* Date */}
                <div style={styles.formRow}>
                  <label style={styles.label}>DATE</label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <input
                      style={{ ...styles.input, width: 'auto', marginBottom: 0 }}
                      type="date"
                      onChange={(e) => {
                        if (e.target.value) {
                          const [year, month, day] = e.target.value.split('-');
                          const date = new Date(year, month - 1, day);
                          const formatted = date.toLocaleDateString('en-US', {
                            month: 'long',
                            day: 'numeric',
                            year: 'numeric',
                          });
                          setForm((prev) => ({ ...prev, date: formatted }));
                        }
                      }}
                    />
                    <input
                      style={{ ...styles.input, flex: 1, marginBottom: 0 }}
                      type="text"
                      placeholder="e.g. May 6, 2026"
                      value={form.date}
                      onChange={(e) => setForm({ ...form, date: e.target.value })}
                    />
                  </div>
                </div>

              </div>

              {/* App toggles */}
              <div style={styles.formRow}>
                <label style={styles.label}>SHOW IN</label>
                <div style={styles.checkRow}>
                  <label style={styles.checkLabel}>
                    <input
                      type="checkbox"
                      checked={form.active_fmp}
                      onChange={(e) => setForm({ ...form, active_fmp: e.target.checked })}
                      style={{ marginRight: '8px' }}
                    />
                    FMP App
                  </label>
                  <label style={styles.checkLabel}>
                    <input
                      type="checkbox"
                      checked={form.active_athletics}
                      onChange={(e) => setForm({ ...form, active_athletics: e.target.checked })}
                      style={{ marginRight: '8px' }}
                    />
                    Gold Card App
                  </label>
                </div>
              </div>

              {/* Preview */}
              {form.headline && (
                <div style={styles.previewWrap}>
                  <div style={styles.previewLabel}>PREVIEW</div>
                  <div style={styles.previewCard}>
                    {form.badge && (
                      <span style={styles.previewBadge}>
                        {form.badge.toUpperCase()}
                      </span>
                    )}
                    <div style={styles.previewHeadline}>{form.headline}</div>
                    {form.date && (
                      <div style={styles.previewDate}>{form.date}</div>
                    )}
                  </div>
                </div>
              )}

              <div style={styles.formBtns}>
                <button type="submit" style={styles.submitBtn} disabled={saving}>
                  {saving ? 'Saving...' : editingId ? 'Save Changes' : 'Add News Item'}
                </button>
                <button type="button" style={styles.cancelBtn} onClick={handleCancel}>
                  Cancel
                </button>
              </div>

            </form>
          </div>
        )}

        {/* NEWS TABLE */}
        <div style={styles.sectionHeader}>
          <h2 style={styles.sectionTitle}>All News Items</h2>
        </div>

        <div style={styles.tableWrap}>
          {newsItems.length === 0 ? (
            <div style={styles.empty}>
              No news items yet. Click "+ Add News" to create one.
            </div>
          ) : (
            <table style={styles.table}>
              <thead>
                <tr>
                  {['Badge', 'Headline', 'Date', 'FMP', 'Gold Card', 'Actions'].map((h) => (
                    <th key={h} style={styles.th}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {newsItems.map((item) => (
                  <tr key={item.id}>
                    <td style={styles.td}>
                      <span style={styles.badge}>{item.badge || '—'}</span>
                    </td>
                    <td style={{ ...styles.td, maxWidth: '400px' }}>
                      {item.headline}
                    </td>
                    <td style={{ ...styles.td, color: '#888', whiteSpace: 'nowrap' }}>
                      {item.date || '—'}
                    </td>
                    <td style={styles.td}>
                      <Toggle
                        value={item.active_fmp}
                        onChange={() => handleToggle(item, 'active_fmp')}
                      />
                    </td>
                    <td style={styles.td}>
                      <Toggle
                        value={item.active_athletics}
                        onChange={() => handleToggle(item, 'active_athletics')}
                      />
                    </td>
                    <td style={styles.td}>
                      <div style={styles.actionBtns}>
                        <button style={styles.editBtn} onClick={() => handleEdit(item)}>
                          Edit
                        </button>
                        <button style={styles.deleteBtn} onClick={() => handleDelete(item.id)}>
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

      </div>
    </div>
  );
}

function Toggle({ value, onChange }) {
  return (
    <div
      onClick={onChange}
      style={{
        width: '36px',
        height: '20px',
        borderRadius: '10px',
        background: value ? GOLD : '#ddd',
        position: 'relative',
        cursor: 'pointer',
        transition: 'background 0.2s',
      }}
    >
      <div style={{
        position: 'absolute',
        width: '14px',
        height: '14px',
        borderRadius: '50%',
        background: '#fff',
        top: '3px',
        left: value ? '19px' : '3px',
        transition: 'left 0.2s',
        boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
      }} />
    </div>
  );
}

const styles = {
  wrap: { display: 'flex', flexDirection: 'column', minHeight: '100vh' },
  loading: { display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' },
  topbar: {
    background: '#fff',
    borderBottom: '1px solid #e0e0e0',
    padding: '16px 24px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    position: 'sticky',
    top: 0,
    zIndex: 10,
  },
  topbarTitle: {
    fontSize: '18px',
    fontWeight: '500',
    color: '#111',
    margin: 0,
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  },
  topbarCount: {
    fontSize: '13px',
    fontWeight: '400',
    color: '#aaa',
  },
  topbarBtn: {
    background: GOLD,
    color: '#111',
    border: 'none',
    borderRadius: '8px',
    padding: '8px 16px',
    fontSize: '13px',
    fontWeight: '500',
    cursor: 'pointer',
  },
  content: { padding: '24px', flex: 1 },
  successBanner: {
    background: '#f0fff4',
    border: '1px solid #86efac',
    borderRadius: '10px',
    padding: '12px 16px',
    marginBottom: '20px',
    color: '#166534',
    fontSize: '14px',
  },
  formCard: {
    background: '#fff',
    border: '1px solid #e0e0e0',
    borderRadius: '14px',
    padding: '24px',
    marginBottom: '24px',
  },
  formTitle: {
    fontSize: '16px',
    fontWeight: '500',
    color: '#111',
    marginBottom: '20px',
  },
  formGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '16px',
    marginBottom: '4px',
  },
  formRow: { marginBottom: '16px' },
  label: {
    display: 'block',
    color: '#888',
    fontSize: '10px',
    letterSpacing: '1px',
    marginBottom: '6px',
  },
  input: {
    width: '100%',
    padding: '10px 12px',
    border: '1px solid #e0e0e0',
    borderRadius: '8px',
    fontSize: '14px',
    color: '#111',
    fontFamily: 'inherit',
    outline: 'none',
    boxSizing: 'border-box',
    marginBottom: '0',
  },
  select: {
    width: '100%',
    padding: '10px 12px',
    border: '1px solid #e0e0e0',
    borderRadius: '8px',
    fontSize: '14px',
    color: '#111',
    fontFamily: 'inherit',
    outline: 'none',
    background: '#fff',
    cursor: 'pointer',
    boxSizing: 'border-box',
  },
  checkRow: {
    display: 'flex',
    gap: '24px',
  },
  checkLabel: {
    display: 'flex',
    alignItems: 'center',
    fontSize: '14px',
    color: '#444',
    cursor: 'pointer',
  },
  previewWrap: {
    marginBottom: '20px',
  },
  previewLabel: {
    color: '#888',
    fontSize: '10px',
    letterSpacing: '1px',
    marginBottom: '8px',
  },
  previewCard: {
    background: '#fff',
    border: '1px solid #e0e0e0',
    borderLeft: `4px solid ${GOLD}`,
    borderRadius: '0 12px 12px 0',
    padding: '14px 16px',
    maxWidth: '400px',
  },
  previewBadge: {
    display: 'inline-block',
    background: 'rgba(255,194,32,0.15)',
    color: '#b8860b',
    fontSize: '9px',
    fontWeight: '600',
    letterSpacing: '1px',
    padding: '3px 8px',
    borderRadius: '20px',
    marginBottom: '8px',
  },
  previewHeadline: {
    fontSize: '14px',
    fontWeight: '500',
    color: '#111',
    marginBottom: '6px',
    lineHeight: '1.4',
  },
  previewDate: {
    fontSize: '12px',
    color: '#888',
  },
  formBtns: {
    display: 'flex',
    gap: '12px',
  },
  submitBtn: {
    background: GOLD,
    color: '#111',
    border: 'none',
    borderRadius: '10px',
    padding: '11px 24px',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
  },
  cancelBtn: {
    background: 'none',
    color: '#888',
    border: '1px solid #e0e0e0',
    borderRadius: '10px',
    padding: '11px 24px',
    fontSize: '14px',
    cursor: 'pointer',
  },
  sectionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '12px',
  },
  sectionTitle: {
    fontSize: '15px',
    fontWeight: '500',
    color: '#111',
    margin: 0,
  },
  tableWrap: {
    background: '#fff',
    border: '1px solid #e0e0e0',
    borderRadius: '12px',
    overflow: 'hidden',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: '13px',
  },
  th: {
    background: '#f9f9f9',
    color: '#888',
    fontSize: '11px',
    padding: '10px 16px',
    textAlign: 'left',
    borderBottom: '1px solid #e0e0e0',
    fontWeight: '500',
    whiteSpace: 'nowrap',
  },
  td: {
    padding: '12px 16px',
    borderBottom: '1px solid #f0f0f0',
    color: '#111',
    verticalAlign: 'middle',
  },
  badge: {
    display: 'inline-block',
    background: 'rgba(255,194,32,0.15)',
    color: '#b8860b',
    fontSize: '10px',
    fontWeight: '600',
    padding: '3px 8px',
    borderRadius: '20px',
    whiteSpace: 'nowrap',
  },
  actionBtns: {
    display: 'flex',
    gap: '6px',
  },
  editBtn: {
    background: 'none',
    border: '1px solid #e0e0e0',
    borderRadius: '6px',
    padding: '4px 10px',
    fontSize: '12px',
    color: '#444',
    cursor: 'pointer',
  },
  deleteBtn: {
    background: 'none',
    border: '1px solid #e0e0e0',
    borderRadius: '6px',
    padding: '4px 10px',
    fontSize: '12px',
    color: '#ff6b6b',
    cursor: 'pointer',
  },
  empty: {
    padding: '32px',
    textAlign: 'center',
    color: '#888',
    fontSize: '13px',
  },
  logoPreview: {
    width: '60px',
    height: '60px',
    objectFit: 'contain',
    borderRadius: '8px',
    border: '1px solid #e0e0e0',
    marginTop: '8px',
    background: '#f9f9f9',
  },
};